import { scheduleReviewRefresh } from './reviewDecisionRefresh';
import type { QueryClient, QueryKey } from "@tanstack/react-query";

/** Shape of `/api/review/pending` cache entries used for optimistic queue updates. */
export type ReviewPendingCachePayload = {
    success: boolean;
    data: Array<{ postingEventId: number }>;
    held?: Array<{ postingEventId: number }>;
    submissionTotal?: number;
    combinedTotal?: number;
    total?: number;
};

/**
 * Remove a decided submission from the pending queue cache immediately (A4 / C2).
 * Skipped when includeReviewed is on — row may remain visible by design.
 */
export function optimisticallyRemoveReviewPendingSubmission(
    queryClient: QueryClient,
    queryKey: QueryKey,
    postingEventId: number,
    options: { includeReviewed: boolean },
): void {
    if (options.includeReviewed) {
        return;
    }
    queryClient.setQueryData<ReviewPendingCachePayload>(queryKey, (prev) => {
        if (!prev) {
            return prev;
        }
        const nextData = prev.data.filter((row) => row.postingEventId !== postingEventId);
        if (nextData.length === prev.data.length) {
            return prev;
        }
        const dec = (n: number | undefined, fallback: number) =>
            Math.max(0, (n ?? fallback) - 1);
        return {
            ...prev,
            data: nextData,
            submissionTotal: dec(prev.submissionTotal, prev.data.length),
            combinedTotal: prev.combinedTotal != null ? dec(prev.combinedTotal, prev.combinedTotal) : prev.combinedTotal,
            total: prev.total != null ? dec(prev.total, prev.total) : prev.total,
        };
    });
}

/** Background refetch after a decision — must not block mutation settle (C1). */
export function scheduleReviewDecisionBackgroundSync(
    queryClient: QueryClient,
    postingEventId: number,
): void {
    scheduleReviewRefresh(queryClient, [
        ["/api/review/pending"],
        ["/api/review/submissions", postingEventId],
        ["campaign-favourite-images"],
        ["tracker-stores"],
    ]);
}
